import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import '../assets/ActivityLog.css';
import '../assets/Pagination.css';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, hasMore: false });
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    userId: '',
    email: '',
    action: [],
    resource: [],
    severity: []
  });
  
  const [expandedLog, setExpandedLog] = useState(null);
  
  // Dropdown visibility states
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showResourceDropdown, setShowResourceDropdown] = useState(false);
  const [showSeverityDropdown, setShowSeverityDropdown] = useState(false);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  
  // Email suggestions
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchSecurityAlerts();
  }, [pagination.offset]);

  useEffect(() => {
    // Fetch email suggestions only once on mount
    fetchEmailSuggestions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-multiselect') && !event.target.closest('.email-search-container')) {
        setShowActionDropdown(false);
        setShowResourceDropdown(false);
        setShowSeverityDropdown(false);
        setShowEmailDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Filter email suggestions based on input
    if (filters.email) {
      const filtered = emailSuggestions.filter(email => 
        email.toLowerCase().includes(filters.email.toLowerCase())
      );
      setFilteredEmails(filtered);
    } else {
      setFilteredEmails(emailSuggestions);
    }
  }, [filters.email, emailSuggestions]);

  const fetchSecurityAlerts = async () => {
    try {
      const response = await adminAPI.get('/security-alerts');
      setSecurityAlerts(response.data.alerts);
      setAlertCount(response.data.count);
    } catch (err) {
      console.error('Failed to load security alerts', err);
    }
  };

  const fetchEmailSuggestions = async () => {
    try {
      // Fetch a sample of logs to get unique emails
      const response = await adminAPI.get('/audit-logs?limit=1000');
      
      if (response.data && response.data.logs && Array.isArray(response.data.logs)) {
        const uniqueEmails = [...new Set(
          response.data.logs
            .map(log => log.userEmail)
            .filter(email => email && email.trim() !== '')
        )].sort();
        
        setEmailSuggestions(uniqueEmails);
        setFilteredEmails(uniqueEmails);
      } else {
        setEmailSuggestions([]);
        setFilteredEmails([]);
      }
    } catch (err) {
      console.error('Failed to load email suggestions:', err);
      setEmailSuggestions([]);
      setFilteredEmails([]);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });
      
      // Add non-array filters
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.email) params.append('email', filters.email);
      
      // Add array filters (multi-select)
      filters.action.forEach(action => params.append('action', action));
      filters.resource.forEach(resource => params.append('resource', resource));
      filters.severity.forEach(severity => params.append('severity', severity));
      
      const response = await adminAPI.get(`/audit-logs?${params}`);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError('Failed to load activity logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      from: '',
      to: '',
      userId: '',
      email: '',
      action: [],
      resource: [],
      severity: []
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const toggleFilterOption = (filterName, value) => {
    setFilters(prev => {
      const currentValues = prev[filterName];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterName]: newValues };
    });
  };

  const handleExport = async (format = 'json') => {
    try {
      const params = new URLSearchParams({
        format,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });
      
      const response = await adminAPI.get(`/audit-logs/export?${params}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit-logs-${new Date().toISOString()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit-logs-${new Date().toISOString()}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      alert('Failed to export logs');
      console.error(err);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'severity-critical';
      case 'WARNING': return 'severity-warning';
      case 'INFO': return 'severity-info';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', { timeZone: 'Asia/Singapore' });
  };

  const toggleExpandLog = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const nextPage = () => {
    if (pagination.hasMore) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const prevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  return (
    <div className="activity-log-container">
      <div className="activity-log-header">
        <h2>Activity Log</h2>
        <div className="export-buttons">
          <button onClick={() => handleExport('json')} className="btn-export">Export JSON</button>
          <button onClick={() => handleExport('csv')} className="btn-export">Export CSV</button>
        </div>
      </div>

      {alertCount > 0 && (
        <div className="security-alerts-banner">
          <span className="alert-icon">⚠️</span>
          <span className="alert-text">
            {alertCount} critical security alert{alertCount > 1 ? 's' : ''} in the last 24 hours
          </span>
          <button 
            onClick={() => {
              setFilters(prev => ({ ...prev, severity: ['CRITICAL'] }));
              handleApplyFilters();
            }}
            className="btn-view-alerts"
          >
            View Alerts
          </button>
        </div>
      )}

      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="datetime-local"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="datetime-local"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Email</label>
            <input
              type="text"
              placeholder="Search by email"
              value={filters.email}
              onChange={(e) => handleFilterChange('email', e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>Action:</label>
            <div className="custom-multiselect">
              <div 
                className="multiselect-trigger"
                onClick={() => setShowActionDropdown(!showActionDropdown)}
              >
                <span>
                  {filters.action.length === 0 
                    ? 'All Actions' 
                    : `${filters.action.length} action${filters.action.length > 1 ? 's' : ''} selected`}
                </span>
                <span className="dropdown-arrow">▼</span>
              </div>
              {showActionDropdown && (
                <div className="multiselect-dropdown">
                  <div className="multiselect-option" onClick={() => setFilters(prev => ({ ...prev, action: [] }))}>
                    <input 
                      type="checkbox" 
                      checked={filters.action.length === 0}
                      readOnly
                    />
                    <span>All Actions</span>
                  </div>
                  {['LOGIN', 'LOGOUT', 'FAILED_LOGIN', 'CREATE_TUTOR', 'UPDATE_TUTOR', 'DELETE_TUTOR', 'ARCHIVE_TUTOR', 'CREATE_TUTEE', 'UPDATE_TUTEE', 'DELETE_TUTEE', 'ARCHIVE_TUTEE', 'CREATE_LESSON', 'EXPORT_DATA', 'UPDATE_PROFILE_IMAGE', 'REMOVE_PROFILE_IMAGE', 'ADMIN_UPDATE_PROFILE_IMAGE', 'ADMIN_REMOVE_PROFILE_IMAGE'].map(action => (
                    <div 
                      key={action} 
                      className="multiselect-option"
                      onClick={() => toggleFilterOption('action', action)}
                    >
                      <input 
                        type="checkbox" 
                        checked={filters.action.includes(action)}
                        readOnly
                      />
                      <span>{action.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="filter-group">
            <label>Resource:</label>
            <div className="custom-multiselect">
              <div 
                className="multiselect-trigger"
                onClick={() => setShowResourceDropdown(!showResourceDropdown)}
              >
                <span>
                  {filters.resource.length === 0 
                    ? 'All Resources' 
                    : `${filters.resource.length} resource${filters.resource.length > 1 ? 's' : ''} selected`}
                </span>
                <span className="dropdown-arrow">▼</span>
              </div>
              {showResourceDropdown && (
                <div className="multiselect-dropdown">
                  <div className="multiselect-option" onClick={() => setFilters(prev => ({ ...prev, resource: [] }))}>
                    <input 
                      type="checkbox" 
                      checked={filters.resource.length === 0}
                      readOnly
                    />
                    <span>All Resources</span>
                  </div>
                  {['Auth', 'Tutor', 'Tutee', 'Lesson', 'System'].map(resource => (
                    <div 
                      key={resource} 
                      className="multiselect-option"
                      onClick={() => toggleFilterOption('resource', resource)}
                    >
                      <input 
                        type="checkbox" 
                        checked={filters.resource.includes(resource)}
                        readOnly
                      />
                      <span>{resource}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="filter-group">
            <label>Severity:</label>
            <div className="custom-multiselect">
              <div 
                className="multiselect-trigger"
                onClick={() => setShowSeverityDropdown(!showSeverityDropdown)}
              >
                <span>
                  {filters.severity.length === 0 
                    ? 'All Severities' 
                    : `${filters.severity.length} selected`}
                </span>
                <span className="dropdown-arrow">▼</span>
              </div>
              {showSeverityDropdown && (
                <div className="multiselect-dropdown">
                  <div className="multiselect-option" onClick={() => setFilters(prev => ({ ...prev, severity: [] }))}>
                    <input 
                      type="checkbox" 
                      checked={filters.severity.length === 0}
                      readOnly
                    />
                    <span>All Severities</span>
                  </div>
                  {['INFO', 'WARNING', 'CRITICAL'].map(severity => (
                    <div 
                      key={severity} 
                      className="multiselect-option"
                      onClick={() => toggleFilterOption('severity', severity)}
                    >
                      <input 
                        type="checkbox" 
                        checked={filters.severity.includes(severity)}
                        readOnly
                      />
                      <span>{severity}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn-apply">Apply Filters</button>
          <button onClick={handleClearFilters} className="btn-clear">Clear Filters</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading activity logs...</div>
      ) : (
        <>
          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Severity</th>
                  <th>IP Address</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-logs">No activity logs found</td>
                  </tr>
                ) : (
                  logs.map(log => (
                    <React.Fragment key={log._id}>
                      <tr className={getSeverityClass(log.severity)}>
                        <td>{formatDate(log.timestamp)}</td>
                        <td>
                          <div className="user-info">
                            <div className="user-email">{log.userEmail || 'N/A'}</div>
                            <div className="user-role">{log.userRole || ''}</div>
                          </div>
                        </td>
                        <td>{log.action}</td>
                        <td>{log.resource}</td>
                        <td>
                          <span className={`severity-badge ${getSeverityClass(log.severity)}`}>
                            {log.severity}
                          </span>
                        </td>
                        <td>{log.ipAddress || 'N/A'}</td>
                        <td>
                          <button 
                            onClick={() => toggleExpandLog(log._id)}
                            className="btn-view-details"
                          >
                            {expandedLog === log._id ? 'Hide' : 'View'} Details
                          </button>
                        </td>
                      </tr>
                      {expandedLog === log._id && (
                        <tr className="expanded-details">
                          <td colSpan="7">
                            <div className="details-content">
                              <div><strong>User Agent:</strong> {log.userAgent || 'N/A'}</div>
                              <div><strong>Resource ID:</strong> {log.resourceId || 'N/A'}</div>
                              {log.details && (
                                <div>
                                  <strong>Additional Details:</strong>
                                  <pre>{JSON.stringify(log.details, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button 
              onClick={prevPage} 
              disabled={pagination.offset === 0}
              className="btn-pagination"
            >
              Previous
            </button>
            <span className="pagination-info">
              Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <button 
              onClick={nextPage} 
              disabled={!pagination.hasMore}
              className="btn-pagination"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityLog;
